import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowUpDownIcon, Filter, MapPin } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import useSearch, { SearchState, SearchAction } from '@/hooks/useSearch';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { Building2, Calendar, Check, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';

import { perks as PERKS } from '@/components/services/PropertyService';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { destinationsList as destinations } from '@/repositories/data/destination';
import { convertToEGP } from '@/lib/currency';
import { areaList } from '@/repositories/data/area';
import { AnimatePresence, motion } from 'framer-motion';
import Perk from '../Properties/Perk';
import Price from '../Properties/Price';

const getDestinationLabel = (name: string) => {
  return destinations.find((dest) => dest.name === name)?.label || name;
};

const getAreaLabel = (destination: string, area: string) => {
  return (
    areaList[destination as keyof typeof areaList]?.find((a) => a.name === area)?.label || area
  );
};

// Active Filters Component
const ActiveFilters = ({
  state,
  dispatch,
  activeFilterCount,
  showAdvancedFilters,
  setShowAdvancedFilters,
}: {
  state: SearchState;
  dispatch: React.Dispatch<SearchAction>;
  activeFilterCount: number;
  showAdvancedFilters: boolean;
  setShowAdvancedFilters: (_show: boolean) => void;
}) => {
  return (
    <div className="flex flex-row justify-between w-full gap-2">
      <div className="flex items-center justify-between w-3/5">
        <div className="flex items-center gap-3">
          {/* Active Filters */}
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-primary">Active filters:</span>
              {state.page > 1 && (
                <Badge variant="secondary" className="text-sm text-nowrap">
                  Page {state.page}
                </Badge>
              )}
              {state.destination && (
                <Badge variant="secondary" className="text-sm text-nowrap">
                  {getDestinationLabel(state.destination)}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => dispatch({ type: 'SET_DESTINATION', payload: null })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              )}
              {state.area && (
                <Badge variant="secondary" className="text-sm text-nowrap">
                  {getAreaLabel(state.destination || '', state.area)}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => dispatch({ type: 'SET_AREA', payload: null })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              )}
              {state.dateRange?.from && (
                <Badge variant="secondary" className="text-sm text-nowrap">
                  {format(state.dateRange.from, 'LLL dd')}
                  {state.dateRange.to && ` - ${format(state.dateRange.to, 'LLL dd')}`}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() =>
                      dispatch({
                        type: 'SET_DATE_RANGE',
                        payload: { from: undefined, to: undefined },
                      })
                    }
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              )}
              {state.guests > 1 && (
                <Badge variant="secondary" className="text-sm text-nowrap">
                  {state.guests}+ {state.guests === 1 ? 'guest' : 'guests'}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => dispatch({ type: 'SET_GUESTS', payload: 1 })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              )}
              {state.minPrice > 0 && state.maxPrice && state.maxPrice > 0 ? (
                <Badge variant="secondary" className="flex text-sm text-nowrap">
                  <span className="mr-1">Between</span>
                  <Price
                    price={convertToEGP(state.minPrice, state.currency)}
                    currency={state.currency}
                  />
                  -
                  <Price
                    price={convertToEGP(state.maxPrice, state.currency)}
                    currency={state.currency}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => {
                      dispatch({ type: 'SET_MIN_PRICE', payload: 0 });
                      dispatch({ type: 'SET_MAX_PRICE', payload: 0 });
                    }}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              ) : null}
              {state.minPrice > 0 &&
              (state.maxPrice == null || state.maxPrice <= 0 || isNaN(state.maxPrice)) ? (
                <Badge variant="secondary" className="text-sm text-nowrap flex">
                  <span className="mr-1">Minimum</span>
                  <Price
                    price={convertToEGP(state.minPrice, state.currency)}
                    currency={state.currency}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => dispatch({ type: 'SET_MIN_PRICE', payload: 0 })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              ) : null}

              {state.maxPrice &&
              state.maxPrice > 0 &&
              (state.minPrice == null || state.minPrice == 0 || isNaN(state.minPrice)) ? (
                <Badge variant="secondary" className="text-sm text-nowrap flex">
                  <span className="mr-1">Maximum</span>
                  <Price
                    price={convertToEGP(state.maxPrice, state.currency)}
                    currency={state.currency}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => dispatch({ type: 'SET_MAX_PRICE', payload: 0 })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              ) : null}

              {state.perks.map((perk: string) => (
                <Badge key={perk} variant="secondary" className="text-sm text-nowrap">
                  {perk}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => dispatch({ type: 'TOGGLE_PERK', payload: perk })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              ))}
              {state.minBeds > 0 && (
                <Badge variant="secondary" className="text-sm text-nowrap">
                  {state.minBeds}+ bedrooms
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => dispatch({ type: 'SET_MIN_BEDS', payload: 0 })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              )}
              {state.minBaths > 0 && (
                <Badge variant="secondary" className="text-sm text-nowrap">
                  {state.minBaths}+ bathrooms
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => dispatch({ type: 'SET_MIN_BATHS', payload: 0 })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-start justify-end w-1/5 gap-2">
        <div className="flex flex-row items-center gap-2">
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dispatch({ type: 'CLEAR_ALL' })}
              className="text-primary hover:text-foreground"
            >
              Clear all
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center gap-2 group relative"
          >
            {activeFilterCount > 0 ? (
              <div className="absolute -top-2 -right-2 bg-brand-blue text-brand-blue text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {activeFilterCount}
              </div>
            ) : null}
            <Filter
              className={`w-4 h-4 transition-transform duration-200 group-hover:rotate-12 ${showAdvancedFilters ? 'rotate-12' : ''}`}
            />{' '}
            Filter
          </Button>
        </div>
        <Select
          value={state.sort || 'trending'}
          onValueChange={(value) => dispatch({ type: 'SET_SORT', payload: value })}
        >
          <SelectTrigger className="w-[160px] bg-white font-milligram font-black rounded-full h-auto uppercase">
            <ArrowUpDownIcon className="w-4 h-4 mr-1" />
            <SelectValue placeholder="Sort by">
              {state.sort === 'trending' && 'Trending'}
              {state.sort === 'newest' && 'Newest'}
              {state.sort === 'updated' && 'Updated'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="trending">Trending</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="updated">Updated</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const DesktopSearchBar = ({ isHero }: { isHero?: boolean }) => {
  const { state, dispatch, activeFilterCount, goToExplore } = useSearch();
  const [isDestinationPopoverOpen, setIsDestinationPopoverOpen] = useState(false);
  const [isAreaPopoverOpen, setIsAreaPopoverOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // clear all when rendered on hompeage
  useEffect(() => {
    if (isHero) {
      dispatch({ type: 'CLEAR_ALL' });
    }
  }, [isHero]);

  return (
    <div className="space-y-6">
      <div
        className={cn(
          'flex items-center gap-3 rounded-xl p-4 shadow-lg max-w-screen-4xl mx-auto w-full bg-gradient-to-r text-white',
          isHero
            ? 'backdrop-blur-lg shadow-2xl backdrop-brightness-99 will-change-[backdrop-filter] will-change-[filter]'
            : 'border border-border shadow-sm'
        )}
      >
        {/* Filters (Destination, Area, Dates, Guests, etc.) */}
        <div className="flex gap-2 justify-start items-center w-full p-0">
          {/* Destination */}
          <div className="flex w-1/4 min-w-0">
            <Popover open={isDestinationPopoverOpen} onOpenChange={setIsDestinationPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  role="combobox"
                  aria-label="Where to?"
                  aria-expanded={isDestinationPopoverOpen}
                  className={cn(
                    'flex w-full justify-start text-left font-normal py-8 rounded-md',
                    !state.destination && 'text-primary'
                  )}
                >
                  <div>
                    <MapPin className={cn('w-8 h-8 mr-2')} color="black" />
                  </div>
                  <div className="flex flex-col items-start">
                    <span className={'text-md '}>Where to?</span>
                    <span className="font-medium text-md">
                      {state.destination ? (
                        <span className="text-primary">
                          {getDestinationLabel(state.destination)}
                        </span>
                      ) : (
                        <span
                          className={cn(
                            isHero ? 'text-primary/70 normal-case' : 'text-primary/70 normal-case'
                          )}
                        >
                          Destinations
                        </span>
                      )}
                    </span>
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 shadow-lg border-border w-[300px]">
                <Command className="backdrop-blur-lg bg-transparent">
                  <CommandInput
                    placeholder="Search for a destination..."
                    className="border-b border-border"
                  />
                  <CommandList>
                    <CommandEmpty>No destination found.</CommandEmpty>
                    <CommandGroup>
                      {destinations.map((destination: { name: string; label: string }) => (
                        <CommandItem
                          key={destination.name}
                          value={destination.name}
                          onSelect={(currentValue) => {
                            dispatch({
                              type: 'SET_DESTINATION',
                              payload: currentValue as string,
                            });
                            setIsDestinationPopoverOpen(false);
                          }}
                          className="hover:bg-muted/50 backdrop-blur-lg bg-white/80"
                        >
                          {state.destination && state.destination === destination.name ? (
                            <Check className={cn('mr-2 h-4 w-4')} />
                          ) : (
                            <div className="w-4 h-4 mr-2" />
                          )}
                          {destination.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="my-4 h-10 w-px bg-black/30 " />

          {/* Area */}
          <div className="flex w-1/4 min-w-0">
            <Popover open={isAreaPopoverOpen} onOpenChange={setIsAreaPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  role="combobox"
                  aria-label="Which area?"
                  aria-expanded={isAreaPopoverOpen}
                  disabled={!state.destination}
                  className={cn(
                    'flex w-full justify-start text-left font-normal py-8 rounded-md',
                    !state.destination && 'opacity-50 cursor-not-allowed',
                    !state.area && 'text-primary'
                  )}
                >
                  <Building2 className={cn('w-8 h-8 mr-2')} color="black" />
                  <div className="flex flex-col items-start">
                    <span className={'text-md'}>WHICH AREA?</span>
                    <span className="font-medium">
                      {state.destination && state.area ? (
                        <span className={cn(isHero ? 'text-black' : 'text-black')}>
                          {getAreaLabel(state.destination, state.area)}
                        </span>
                      ) : (
                        <span
                          className={cn(
                            isHero ? 'text-black normal-case' : 'text-black normal-case'
                          )}
                        >
                          Any area
                        </span>
                      )}
                    </span>
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 shadow-lg border-border w-[300px]">
                <Command>
                  <CommandInput
                    placeholder="Search for an area..."
                    className="border-b border-border"
                  />
                  <CommandList>
                    <CommandEmpty>No areas found for this destination.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        key="any-area"
                        value="any-area"
                        onSelect={() => {
                          dispatch({ type: 'SET_AREA', payload: null });
                          setIsAreaPopoverOpen(false);
                        }}
                        className="hover:bg-muted/50"
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            !state.area ? 'opacity-100 text-primary' : 'opacity-0'
                          )}
                        />
                        Any area
                      </CommandItem>
                      {areaList[state.destination as keyof typeof areaList]?.map((area) => (
                        <CommandItem
                          key={area.name}
                          value={area.name as string}
                          onSelect={(currentValue) => {
                            dispatch({ type: 'SET_AREA', payload: currentValue as string });
                            setIsAreaPopoverOpen(false);
                          }}
                          className="hover:bg-muted/50"
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              state.area && state.area === area.name
                                ? 'opacity-100 text-primary'
                                : 'opacity-0'
                            )}
                          />
                          {area.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="my-4 h-10 w-px bg-black/30 " />

          {/* Dates */}
          <div className="flex w-1/4 min-w-0">
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    'w-full justify-start text-left font-normal py-8 rounded-md',
                    !state.dateRange && 'text-primary'
                  )}
                >
                  <Calendar
                    className={cn(
                      'w-8 h-8 mr-2 text-black',
                      state.dateRange?.to && 'text-foreground'
                    )}
                  />
                  <div className="flex flex-col items-start">
                    <span className={cn('text-sm ')}>When?</span>
                    <span className="font-medium ">
                      {state.dateRange?.from ? (
                        state.dateRange.to ? (
                          <span
                            className={cn(isHero ? 'text-black' : 'text-black')}
                          >{`${format(state.dateRange.from, 'LLL dd')} - ${format(state.dateRange.to, 'LLL dd')}`}</span>
                        ) : (
                          <span className={cn(isHero ? 'text-black' : 'text-black')}>
                            {format(state.dateRange.from, 'LLL dd, y')}
                          </span>
                        )
                      ) : (
                        <span
                          className={cn(
                            isHero
                              ? 'text-black text-primary/70 normal-case'
                              : 'text-black text-primary/70 normal-case'
                          )}
                        >
                          Anytime
                        </span>
                      )}
                    </span>
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="p-3">
                  <CalendarComponent
                    mode="range"
                    min={1}
                    className="text-xl"
                    selected={state.dateRange}
                    disabled={{
                      from: new Date(new Date().setFullYear(new Date().getFullYear() - 10)),
                      to: new Date(new Date().setDate(new Date().getDate() - 1)),
                    }}
                    onClose={() => setIsCalendarOpen(false)}
                    onSelect={(value) => {
                      dispatch({ type: 'SET_DATE_RANGE', payload: value as DateRange });
                    }}
                    numberOfMonths={2}
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="my-4 h-10 w-px bg-black/30 " />

          {/* Guests */}
          <div className="flex w-1/4 min-w-0">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  aria-label="Who's coming?"
                  className={cn(
                    'flex  gap-1 w-full justify-start text-left font-normal py-8 rounded-md',
                    !state.guests && 'text-primary'
                  )}
                >
                  <Users className="w-8 h-8" color="black" />
                  <div className="flex flex-col items-start">
                    <span className={cn('text-sm')}>Who's coming?</span>
                    <span
                      className={cn(
                        'font-medium text-primary/70 normal-case',
                        isHero ? 'text-black text-primary/70' : 'text-black'
                      )}
                    >
                      {state.guests} {state.guests === 1 ? 'guest' : 'guests'}
                    </span>
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 w-full mx-auto justify-around">
                    <Button
                      variant="outline"
                      aria-label="Decrease number of guests"
                      size="icon"
                      onClick={() => {
                        if (state.guests > 1) {
                          dispatch({ type: 'SET_GUESTS', payload: state.guests - 1 });
                        }
                      }}
                      className="focus-visible:ring-2 focus-visible:ring-primary transition-all duration-200"
                    >
                      -
                    </Button>
                    <span>{state.guests}</span>
                    <Button
                      variant="outline"
                      aria-label="Increase number of guests"
                      size="icon"
                      onClick={() => {
                        if (state.guests < 10) {
                          dispatch({ type: 'SET_GUESTS', payload: state.guests + 1 });
                        }
                      }}
                      className="focus-visible:ring-2 focus-visible:ring-primary transition-all duration-200"
                    >
                      +
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {isHero ? (
            <div className="flex min-w-0 items-center justify-center gap-2">
              <Button
                variant="default"
                size="lg"
                aria-label="Go to explore"
                className="group"
                onClick={() => {
                  console.log('goToExplore', state);
                  goToExplore({
                    destination: state.destination,
                    area: state.area,
                    dateRange: state.dateRange,
                    guests: state.guests,
                  });
                }}
              >
                <span>Yalla</span>
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      {!isHero ? (
        <ActiveFilters
          state={state}
          dispatch={dispatch}
          activeFilterCount={activeFilterCount}
          showAdvancedFilters={showAdvancedFilters}
          setShowAdvancedFilters={setShowAdvancedFilters}
        />
      ) : null}

      {!isHero ? (
        <AnimatePresence>
          {showAdvancedFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-background rounded-lg p-4 space-y-4 shadow-md border border-border"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Price Range</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      min="0"
                      className="flex-1 px-3 py-2 border border-border rounded-md text-sm"
                      value={state.minPrice || ''}
                      onChange={(e) => {
                        const minValue = parseInt(e.target.value) || 0;
                        dispatch({ type: 'SET_MIN_PRICE', payload: minValue });

                        // If max price is less than new min price, clear max price
                        if (state.maxPrice && state.maxPrice <= minValue) {
                          dispatch({ type: 'SET_MAX_PRICE', payload: 0 });
                        }
                      }}
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      min={state.minPrice ? state.minPrice + 1 : 1}
                      className="flex-1 px-3 py-2 border border-border rounded-md text-sm"
                      value={state.maxPrice || ''}
                      onChange={(e) => {
                        const maxValue = parseInt(e.target.value) || 0;
                        dispatch({ type: 'SET_MAX_PRICE', payload: maxValue });
                      }}
                    />
                  </div>
                  {state.minPrice && state.maxPrice && state.maxPrice <= state.minPrice ? (
                    <p className="text-xs text-red-500 mt-1">
                      Maximum price must be higher than minimum price
                    </p>
                  ) : null}
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Bedrooms</Label>
                  <Select
                    value={state.minBeds.toString()}
                    onValueChange={(value) =>
                      dispatch({ type: 'SET_MIN_BEDS', payload: parseInt(value) })
                    }
                  >
                    <SelectTrigger className="w-full px-3 py-2 border border-border rounded-md text-sm">
                      <SelectValue placeholder="Any" className="text-sm" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Any</SelectItem>
                      <SelectItem value="1">1+</SelectItem>
                      <SelectItem value="2">2+</SelectItem>
                      <SelectItem value="3">3+</SelectItem>
                      <SelectItem value="4">4+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Bathrooms</Label>
                  <Select
                    value={state.minBaths.toString()}
                    onValueChange={(value) =>
                      dispatch({ type: 'SET_MIN_BATHS', payload: parseInt(value) })
                    }
                  >
                    <SelectTrigger className="w-full px-3 py-2 border border-border rounded-md text-sm">
                      <SelectValue placeholder="Any" className="text-sm" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Any</SelectItem>
                      <SelectItem value="1">1+</SelectItem>
                      <SelectItem value="2">2+</SelectItem>
                      <SelectItem value="3">3+</SelectItem>
                      <SelectItem value="4">4+</SelectItem>
                      <SelectItem value="5">5+</SelectItem>
                      <SelectItem value="6">6+</SelectItem>
                      <SelectItem value="7">7+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Perks</Label>
                <div className="flex flex-row flex-wrap gap-2">
                  {PERKS.map((perk) => (
                    <Perk
                      key={perk}
                      perk={perk}
                      onClick={() => dispatch({ type: 'TOGGLE_PERK', payload: perk })}
                      type={state.perks.includes(perk) ? 'default' : 'outline'}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      ) : null}
    </div>
  );
};
